import React, { useState, useMemo, useRef, useEffect } from 'react';
import { hot } from 'react-hot-loader';
import Checkbox from '../../components/Elements/Checkbox/Checkbox';
import Input from '../../components/Elements/Input/Input';
import Upload from '../../components/Elements/Upload/Upload';
import Select from '../../components/Elements/Select/Select';
import TextArea from '../../components/Elements/TextArea/TextArea';
import Button from '../../components/Elements/Button/Button';
import s from './ZohoForm.module.scss';
import { translate } from '../../utils/locale';
import { fieldValidation } from '../../utils/string';
import { fetchSelf, fetchSelfForm } from '../../utils/async';
import { purifier, scrollTo } from '../../utils/dom';

/*
 * Maps lowercase HTML attribute names to their React prop equivalents.
 */
const HTML_ATTR_MAP = {
	class: 'className',
	for: 'htmlFor',
	'accept-charset': 'acceptCharset',
	enctype: 'encType',
	maxlength: 'maxLength',
	readonly: 'readOnly',
	tabindex: 'tabIndex',
	colspan: 'colSpan',
	rowspan: 'rowSpan',
	crossorigin: 'crossOrigin',
	// iframe-specific
	frameborder: 'frameBorder',
	allowfullscreen: 'allowFullScreen',
	marginheight: 'marginHeight',
	marginwidth: 'marginWidth',
	srcdoc: 'srcDoc',
};

/*
 * Non-standard / Zoho-specific attributes that should not be forwarded to
 * React elements (they would cause unknown-prop warnings).
 */
const SKIP_ATTRS = new Set(['fieldtype']);

const toReactAttrName = (name) => {
	const lower = name.toLowerCase();
	if (SKIP_ATTRS.has(lower)) return null;
	return HTML_ATTR_MAP[lower] || name;
};

const parseCssString = (css) => {
	const style = {};
	css.split(';').forEach((decl) => {
		const idx = decl.indexOf(':');
		if (idx === -1) return;
		const prop = decl.slice(0, idx).trim();
		const val = decl.slice(idx + 1).trim();
		if (!prop) return;
		style[prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase())] = val;
	});
	return style;
};

const getNodeAttrs = (node) => {
	const attrs = {};
	Array.from(node.attributes).forEach(({ name, value }) => {
		const reactName = toReactAttrName(name);
		if (!reactName) return;
		attrs[reactName] = reactName === 'style' ? parseCssString(value) : value;
	});
	return attrs;
};

/*
 * Converts a <select> element's <option> children into the array format
 * expected by the Select component: [{ key, value }, ...].
 */
const parseSelectOptions = (selectEl) =>
	Array.from(selectEl.querySelectorAll('option')).map((opt) => ({
		key: opt.value || opt.textContent.trim(),
		value: opt.textContent.trim(),
	}));

const getInitialSelectedOption = (selectEl, options) => {
	const selectedOpt = selectEl.querySelector('option[selected]');
	if (selectedOpt) return selectedOpt.value || selectedOpt.textContent.trim();
	return options[0]?.key || '';
};

/*
 * Walks the parsed form element and builds the initial controlled-state
 * object, keyed by each field's name attribute.
 */
const buildInitialValues = (setup, formEl) => {
	const values = {};
	formEl.querySelectorAll('input:not([type="hidden"]), select, textarea').forEach((el) => {
		const name = el.name || el.id;
		if (!name) return;
		if (el.tagName.toLowerCase() === 'select') {
			const opts = parseSelectOptions(el);
			if (/countr/i.test(name)) {
				values[name] = setup.countries[setup.country]?.name || getInitialSelectedOption(el, opts);
			} else {
				values[name] = getInitialSelectedOption(el, opts);
			}
		} else if (el.tagName.toLowerCase() === 'textarea') {
			values[name] = el.textContent || '';
		} else if (el.tagName.toLowerCase() === 'input' && el.type === 'checkbox') {
			values[name] = el.checked || false;
		} else {
			values[name] = el.value || '';
		}
	});
	return values;
};

/**
 * ZohoForm
 *
 * Receives a raw Zoho HTML form blob via the `htmlBlob` prop, parses it,
 * and replaces native form controls with the project's design-system
 * components (Input, Select, Button) while preserving all other markup
 * (headings, paragraphs, labels, etc.) as-is.
 *
 * All original form attributes (action, method, enctype, …) are preserved
 * so that native form submission to Zoho continues to work.
 */
/*
 * Requests a reCaptcha v3 token for the given site key.
 * Resolves with the token string, or rejects if grecaptcha is not available.
 */
const getRecaptchaToken = (siteKey) => new Promise((resolve, reject) => {
	if (typeof window === 'undefined' || !window.grecaptcha) {
		return reject(new Error('reCaptcha not loaded'));
	}

	const grecaptchaApi = window.grecaptcha;
	const enterpriseApi = grecaptchaApi.enterprise;
	let ready = null;
	let execute = null;

	if (enterpriseApi && typeof enterpriseApi.ready === 'function' && typeof enterpriseApi.execute === 'function') {
		ready = enterpriseApi.ready.bind(enterpriseApi);
		execute = enterpriseApi.execute.bind(enterpriseApi);
	} else if (typeof grecaptchaApi.ready === 'function' && typeof grecaptchaApi.execute === 'function') {
		ready = grecaptchaApi.ready.bind(grecaptchaApi);
		execute = grecaptchaApi.execute.bind(grecaptchaApi);
	}

	if (!ready || !execute) {
		return reject(new Error('reCaptcha API not ready'));
	}

	return ready(() => {
		execute(siteKey, { action: 'submit' }).then(resolve).catch(reject);
	});
});

const ZohoForm = ({ setup, htmlBlob, multistep = false, callback = false, successMessage = '', redirect = '' }) => {
	const recaptchaSiteKey = process.env.RECAPTCHA_SITE_KEY;
	// Parse the HTML blob once (or whenever htmlBlob changes).
	// If the blob contains an <iframe> it takes priority over the full form
	// rendering path — the iframe is rendered as-is (escape hatch for the client).
	const { formEl, iframeEl } = useMemo(() => {
		if (!htmlBlob || typeof document === 'undefined') return { formEl: null, iframeEl: null };
		const parser = new DOMParser();
		const doc = parser.parseFromString(htmlBlob, 'text/html');
		return {
			formEl: doc.querySelector('form'),
			iframeEl: doc.querySelector('iframe'),
		};
	}, [htmlBlob]);

	// Controlled state for all visible form fields, initialised from the
	// parsed DOM so default / pre-selected values are respected.
	const [values, setValues] = useState(() => (formEl ? buildInitialValues(setup, formEl) : {}));

	const [step, setStep] = useState(0);
	const [errors, setErrors] = useState({});
	const [submitted, setSubmitted] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState(false);
	const formRef = useRef(null);
	const wrapperRef = useRef(null);

	useEffect(() => {
		if (submitted && wrapperRef.current) {
			const el = wrapperRef.current;
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					if (!redirect) scrollTo(300, el.getBoundingClientRect().top + window.pageYOffset - 200);
				});
			});
		}
	}, [submitted]);

	const handleChange = (id, value) => {
		setValues((prev) => ({ ...prev, [id]: value }));
		setErrors((prev) => ({ ...prev, [id]: false }));
	};

	// ── iframe escape hatch ────────────────────────────────────────────────
	// When the blob is just an <iframe> embed, skip all form logic and render
	// the iframe directly inside the standard wrapper.
	if (iframeEl) {
		const attrs = getNodeAttrs(iframeEl);
		return (
			<div className={s.zohoForm}>
				<iframe {...attrs} title={attrs.title || 'Embedded form'} />
			</div>
		);
	}

	if (!formEl) return null;

	const isFormControl = (node) => {
		if (node.nodeType !== Node.ELEMENT_NODE) return false;
		const tag = node.tagName.toLowerCase();
		const type = (node.getAttribute('type') || '').toLowerCase();
		return (tag === 'input' && type !== 'hidden') || tag === 'select' || tag === 'textarea';
	};

	const isStandaloneLabel = (node) =>
		node.nodeType === Node.ELEMENT_NODE &&
		node.tagName.toLowerCase() === 'label' &&
		!node.getAttribute('for');

	/*
	 * GROUP_LABELS – section-heading labels that introduce a group of fields.
	 * These are suppressed from rendering (they would appear as orphan <label>
	 * elements); each field within the group uses its own trailing label instead.
	 *
	 * Match is case-insensitive and strips trailing asterisk/whitespace so
	 * "Name *" and "Location" both normalise correctly.
	 */
	const GROUP_LABELS = new Set(['name', 'location', 'address', 'phone']);

	const isGroupLabel = (node) => {
		const normalised = node.textContent.trim().toLowerCase().replace(/[\s*]+$/, '').trim();
		return GROUP_LABELS.has(normalised);
	};

	const groupLabelIsRequired = (node) => node.textContent.includes('*');

	/*
	 * GROUPED_FIELD_PREFIXES – fields whose name attribute starts with one of
	 * these prefixes are considered "grouped". Grouped fields have their label
	 * placed AFTER the input in Zoho's HTML, so Pass 1 (trailing-label look-
	 * behind) applies to them. All other fields have a preceding label and are
	 * handled by Pass 2.
	 *
	 * Basing this on the field name rather than on DOM proximity means the rule
	 * is unaffected by changes to label text or form structure.
	 */
	const GROUPED_FIELD_PREFIXES = [/^Name\d*_.*$/, /^Address\d*_.*$/, /^PhoneNumber\d*_.*$/];

	const isGroupedControl = (node) => {
		const name = node.getAttribute('name') || '';
		return GROUPED_FIELD_PREFIXES.some((prefix) => prefix.test(name));
	};

	/*
	 * Scans an array of sibling nodes and builds two maps:
	 *   labelForControl : nodeIndex → label text string
	 *   consumedLabels  : Set of label nodeIndices that have been dropped
	 *
	 * Pass -1 – explicit for-attribute labels.
	 *   Handles: <input id="DecisionBox"> … <label for="DecisionBox">text</label>
	 *   These are consumed immediately; no other pass will touch them.
	 *
	 * Pass 0 – required inheritance from group labels.
	 *   A group label marked with * (e.g. "Name *") passes the required marker
	 *   down to every control in its scope (until the next group label).
	 *
	 * Pass 1 – trailing labels (look-behind) take priority.
	 *   Handles: <input> followed by <label>First Name</label>
	 *
	 * Pass 2 – preceding labels (look-ahead) fill in controls still unlabelled.
	 *   Handles: <label>E-mail *</label> followed by <input>
	 *
	 * Group labels are consumed (dropped) last so they never render as orphan
	 * <label> elements in the output.
	 */
	const buildLabelMap = (nodes) => {
		const labelForControl = new Map();
		const consumedLabels = new Set();
		const requiredByGroup = new Set();

		// Pass -1: explicit for-attribute labels — highest priority.
		// Handles: <input id="DecisionBox"> … <label for="DecisionBox">text</label>
		nodes.forEach((node, i) => {
			if (node.nodeType !== Node.ELEMENT_NODE) return;
			if (node.tagName.toLowerCase() !== 'label') return;
			const forAttr = node.getAttribute('for');
			if (!forAttr) return;
			const targetIdx = nodes.findIndex(
				(n) => n.nodeType === Node.ELEMENT_NODE && n.getAttribute('id') === forAttr
			);
			if (targetIdx !== -1 && !labelForControl.has(targetIdx)) {
				labelForControl.set(targetIdx, node.textContent.trim());
				consumedLabels.add(i);
			}
		});

		// Pass 0: mark grouped controls whose section heading carried a required
		// marker (*). Walk forward tracking the most recent group label; when a
		// grouped control (Name_ / Address_ prefix) is encountered and the current
		// group heading was required, record that control index.
		let currentGroupRequired = false;
		nodes.forEach((node, i) => {
			if (isStandaloneLabel(node) && isGroupLabel(node)) {
				currentGroupRequired = groupLabelIsRequired(node);
			} else if (isFormControl(node) && isGroupedControl(node) && currentGroupRequired) {
				requiredByGroup.add(i);
			}
		});

		// Find the nearest element node scanning in direction +1 or -1,
		// stopping early if a non-whitespace text node interrupts the search.
		const nearestElement = (startIdx, dir) => {
			let j = startIdx;
			while (j >= 0 && j < nodes.length) {
				const n = nodes[j];
				if (n.nodeType === Node.ELEMENT_NODE) return { node: n, index: j };
				if (n.nodeType === Node.TEXT_NODE && n.textContent.trim()) return null;
				j += dir;
			}
			return null;
		};

		// Pass 1: trailing labels → look behind for the nearest unlabelled control,
		// but ONLY when that control is a grouped field (name starts with Name_ or
		// Address_). All other fields are labelled by their preceding label in Pass 2.
		nodes.forEach((node, i) => {
			if (!isStandaloneLabel(node) || isGroupLabel(node) || consumedLabels.has(i)) return;
			const prev = nearestElement(i - 1, -1);
			if (prev && isFormControl(prev.node) && isGroupedControl(prev.node) && !labelForControl.has(prev.index)) {
				labelForControl.set(prev.index, node.textContent.trim());
				consumedLabels.add(i);
			}
		});

		// Pass 2: preceding labels → look ahead for controls still without a label.
		const precedingLabelIndices = new Set();
		nodes.forEach((node, i) => {
			if (!isStandaloneLabel(node) || isGroupLabel(node) || consumedLabels.has(i)) return;
			const next = nearestElement(i + 1, 1);
			if (next && isFormControl(next.node) && !labelForControl.has(next.index)) {
				labelForControl.set(next.index, node.textContent.trim());
				consumedLabels.add(i);
				precedingLabelIndices.add(i);
			}
		});

		// Drop group labels — they must not be rendered as orphan <label> elements.
		nodes.forEach((node, i) => {
			if (isStandaloneLabel(node) && isGroupLabel(node)) consumedLabels.add(i);
		});

		// Apply required marker inherited from group labels to their field labels.
		requiredByGroup.forEach((controlIdx) => {
			const label = labelForControl.get(controlIdx);
			if (label && !label.includes('*') && !label.includes('2')) {
				labelForControl.set(controlIdx, `${label} *`);
			}
		});

		return { labelForControl, consumedLabels, precedingLabelIndices };
	};

	/*
	 * Scans all form controls and returns:
	 *   requiredFields : Set of field names whose resolved label contains "*"
	 *   fieldTypes     : Map of field name → validation type string for fieldValidation()
	 */
	const buildFieldMeta = () => {
		const nodes = Array.from(formEl.childNodes);
		const { labelForControl } = buildLabelMap(nodes);
		const requiredFields = new Set();
		const fieldTypes = new Map();

		nodes.forEach((node, i) => {
			if (!isFormControl(node)) return;
			const name = node.getAttribute('name');
			if (!name) return;
			const label = labelForControl.get(i) || '';
			if (label.includes('*')) requiredFields.add(name);
			const isSelect = node.tagName.toLowerCase() === 'select';
			const isTextarea = node.tagName.toLowerCase() === 'textarea';
			const rawType = isSelect ? 'select' : (node.getAttribute('type') || 'text').toLowerCase();
			// Map to fieldValidation type strings
			let type = 'text';
			if (rawType === 'email' || /email/i.test(name)) type = 'email';
			else if (/phone|mobile/i.test(name)) type = 'phone';
			else if (isSelect) type = 'select';
			else if (isTextarea) type = 'text';
			fieldTypes.set(name, type);
		});

		return { requiredFields, fieldTypes };
	};

	const { requiredFields, fieldTypes } = buildFieldMeta();

	/*
	 * Validates a single field. Normalises the select placeholder to empty so
	 * fieldValidation() treats an unselected dropdown as required-but-empty.
	 * Returns a human-readable error string, or false if valid.
	 */
	const validateField = (name, value) => {
		if (!requiredFields.has(name)) return false;
		const type = fieldTypes.get(name) || 'text';
		const normalised = (type === 'select' && (value === '-Select-' || !value)) ? '' : (value || '');
		const errorCode = fieldValidation(normalised, type);
		return errorCode && translate(setup, `error${errorCode}`);
	};

	/*
	 * Validates a collection of field names against current values.
	 * Returns a partial errors map { [name]: errorString | false }.
	 * Only includes fields that actually have an error.
	 */
	const validateFieldNames = (names) => {
		const result = {};
		names.forEach((name) => {
			const error = validateField(name, values[name]);
			if (error) result[name] = error;
		});
		return result;
	};

	const handleBlur = (id, value) => {
		const error = validateField(id, value);
		if (error) setErrors((prev) => ({ ...prev, [id]: error }));
	};

	const handleFocus = (id) => {
		setErrors((prev) => ({ ...prev, [id]: false }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		const allRequired = Array.from(requiredFields);
		const submitErrors = validateFieldNames(allRequired);
		if (Object.keys(submitErrors).length > 0) {
			setErrors((prev) => ({ ...prev, ...submitErrors }));
			return;
		}

		setSubmitting(true);
		setSubmitError(false);

		// Collect Zoho hidden metadata fields + visible field values.
		const fields = {};
		formEl.querySelectorAll('input[type="hidden"]').forEach((input) => {
			fields[input.name] = input.value || '';
		});
		Object.entries(values).forEach(([name, value]) => {
			fields[name] = value;
		});

		try {
			// Always use FormData to match Zoho's multipart/form-data requirement
			const formData = new FormData();
			formData.append('zohoAction', formEl.getAttribute('action'));
			if (recaptchaSiteKey) {
				formData.append('recaptchaToken', await getRecaptchaToken(recaptchaSiteKey));
			}
			Object.entries(fields).forEach(([name, value]) => {
				if (typeof FileList !== 'undefined' && value instanceof FileList) {
					// Append files from FileList, or create empty file field if no files selected
					if (value.length > 0) {
						Array.from(value).forEach((file) => formData.append(name, file));
					} else {
						// Empty file input - append empty file to match browser behavior
						formData.append(name, new File([], '', { type: 'application/octet-stream' }));
					}
				} else if (typeof value === 'boolean') {
					// Checkboxes: convert true to "on" to match browser behavior
					formData.append(name, value ? 'on' : '');
				} else {
					formData.append(name, value);
				}
			});
			const result = await fetchSelfForm('zoho-form', formData);

			if (result && result.success) {
				setSubmitted(true);
				if (redirect) window.location.href = redirect.url;
			} else {
				setSubmitError(true);
			}
		} catch (err) {
			setSubmitError(true);
		} finally {
			setSubmitting(false);
			if (callback) {
				setTimeout(() => {
					callback();
				}, 10000);
			}
		}
	};

	/*
	 * Recursively converts a DOM node to a React element.
	 * An optional `label` string is forwarded to Input / Select components.
	 */
	const renderNode = (node, index, label = null) => {
		// Text nodes
		if (node.nodeType === Node.TEXT_NODE) {
			const text = node.textContent;
			return text.trim() ? text : null;
		}

		if (node.nodeType !== Node.ELEMENT_NODE) return null;

		const tag = node.tagName.toLowerCase();
		const name = node.getAttribute('name');

		// ── Hidden inputs ─────────────────────────────────────────────────────
		// These carry Zoho metadata and must be submitted untouched.
		if (tag === 'input' && (node.getAttribute('type') || '').toLowerCase() === 'hidden') {
			return <input key={index} type="hidden" name={node.getAttribute('name')} value={node.getAttribute('value') || ''} readOnly />;
		}

		// ── Select → <Select> ────────────────────────────────────────────────
		if (tag === 'select' || /region/i.test(name)) {
			let options = [];
			if (/region/i.test(name)) {
				const countryFieldName = Object.keys(values).find((k) => /countr/i.test(k));
				const countryName = countryFieldName ? values[countryFieldName] : setup.country;
				const countryIso = Object.keys((setup?.countryData || {})).find((iso) => setup.countryData[iso].name === countryName);
				const statesData = setup?.countryData?.[countryIso]?.states;
				if (statesData) {
					options = [
						{ key: '', value: translate(setup, 'chooseState') },
						...Object.keys(statesData).map((k) => ({ key: statesData[k], value: statesData[k] })),
					];
				}
			} else {
				options = parseSelectOptions(node);
			}

			if (options.length) return <Select key={index} id={name} options={options} selected={values[name] !== undefined ? values[name] : (options[0]?.key || '')} label={label} isError={errors[name] || false} inputChange={handleChange} inputBlur={handleBlur} />;
		}

		// ── File inputs → <Upload> ──────────────────────────────────────────
		if (tag === 'input' && (node.getAttribute('type') || '').toLowerCase() === 'file') {
			const multiple = node.hasAttribute('multiple') || null;
			const accept = node.getAttribute('accept') || null;
			const extraProps = {};
			if (multiple) extraProps.multiple = true;
			if (accept) extraProps.accept = accept;
			return <Upload key={index} id={name} label={label} buttonLabel={translate(setup, 'labelButtonUploadFile')} buttonInfoLabel={translate(setup, 'labelButtonUploadInfo')} inputChange={handleChange} error={errors[name] || false} {...extraProps} />;
		}

		// ── Checkbox inputs → <Checkbox> ────────────────────────────────────
		if (tag === 'input' && (node.getAttribute('type') || '').toLowerCase() === 'checkbox') {
			return <Checkbox key={index} id={name} label={label} selected={values[name] === true} inputChange={handleChange} isError={errors[name] || false} />;
		}

		// ── Visible inputs → <Input> ─────────────────────────────────────────
		if (tag === 'input') {
			let type = 'text';
			if (node.getAttribute('type')) type = node.getAttribute('type');
			if (/email/i.test(name)) type = 'email';
			if (/phone|mobile/i.test(name)) {
				type = 'phone';
				label = translate(setup, 'labelPhone');
			}
			const placeholder = node.getAttribute('placeholder') || '';
			const extraAttrs = {};

			const maxLength = node.getAttribute('maxlength');
			if (maxLength) extraAttrs.maxLength = parseInt(maxLength, 10);

			return <Input key={index} id={name} type={type} value={values[name] !== undefined ? values[name] : ''} placeholder={placeholder} label={label} isError={errors[name] || false} inputChange={handleChange} inputBlur={handleBlur} inputFocus={handleFocus} {...extraAttrs} />;
		}

		// ── Textarea → <TextArea> ────────────────────────────────────────────
		if (tag === 'textarea') {
			const placeholder = node.getAttribute('placeholder') || '';
			const extraAttrs = {};
			const maxLength = node.getAttribute('maxlength');
			if (maxLength) extraAttrs.maxLength = parseInt(maxLength, 10);
			const rows = node.getAttribute('rows');
			if (rows) extraAttrs.rows = parseInt(rows, 10);
			const cols = node.getAttribute('cols');
			if (cols) extraAttrs.cols = parseInt(cols, 10);
			return <TextArea key={index} id={name} value={values[name] !== undefined ? values[name] : ''} placeholder={placeholder} label={label} isError={errors[name] || false} inputChange={handleChange} inputBlur={handleBlur} extraAttrs={extraAttrs} />;
		}

		// ── Button → <Button> ────────────────────────────────────────────────
		if (tag === 'button') {
			const type = node.getAttribute('type') || 'button';
			return <Button variantClasses={['button__submit', Object.keys(errors).filter((key) => errors[key]).length > 0 || submitError ? 'button__submit__error' : '']} key={index} type={type}>{ node.textContent.trim() }</Button>;
		}

		// ── All other elements (h2, p, label, em, …) ─────────────────────────
		// Recurse into children so nested form controls are still replaced.
		// eslint-disable-next-line no-use-before-define
		const children = renderChildren(node.childNodes);
		const attrs = { key: index, ...getNodeAttrs(node) };

		return React.createElement(tag, attrs, ...children);
	};

	/*
	 * Renders a NodeList as an array of React elements, pairing standalone
	 * <label> elements with their nearest input/select and passing the label
	 * text into those components directly.
	 */
	const renderChildren = (nodeList) => {
		const nodes = Array.from(nodeList);
		const { labelForControl, consumedLabels } = buildLabelMap(nodes);

		return nodes
			.map((node, i) => {
				if (consumedLabels.has(i)) return null;
				const label = labelForControl.get(i) || null;
				return renderNode(node, i, label);
			})
			.filter((c) => c !== null && c !== undefined);
	};

	/*
	 * Segments form child nodes into discrete steps for multistep mode.
	 *
	 * Step boundaries are triggered by:
	 *   - A group label (e.g. "Name *") — the label is dropped, its controls
	 *     become a new step.
	 *   - A preceding standalone label (e.g. "E-mail *") — consumed in pass 2
	 *     of buildLabelMap; signals that a single-control step follows.
	 *
	 * Hidden inputs are returned separately so they can be rendered in all
	 * steps (required for form submission).
	 * The submit button is stripped here and appended to the final step.
	 */
	const buildSteps = (nodeList) => {
		const nodes = Array.from(nodeList);
		const { labelForControl, consumedLabels, precedingLabelIndices } = buildLabelMap(nodes);

		const steps = [];
		const stepFields = []; // array of Set<fieldName> — one per step
		const hiddenElements = [];
		let submitButton = null;
		let currentStep = [];
		let currentStepFields = new Set();
		let preamble = []; // static nodes before the first form control
		let firstStepStarted = false;

		const flushStep = () => {
			if (currentStep.length) {
				steps.push(currentStep);
				stepFields.push(currentStepFields);
				currentStep = [];
				currentStepFields = new Set();
			}
		};

		nodes.forEach((node, i) => {
			if (node.nodeType !== Node.ELEMENT_NODE && node.nodeType !== Node.TEXT_NODE) return;

			const tag = node.nodeType === Node.ELEMENT_NODE ? node.tagName.toLowerCase() : null;
			const inputType = tag === 'input' ? (node.getAttribute('type') || '').toLowerCase() : null;

			// Hidden inputs rendered outside steps so they submit with the form.
			if (tag === 'input' && inputType === 'hidden') {
				hiddenElements.push(renderNode(node, `h${i}`));
				return;
			}

			// Capture the submit button for the last step.
			if (tag === 'button') {
				submitButton = renderNode(node, i);
				return;
			}

			// Group label → flush current step and start a new one (label not rendered).
			if (isStandaloneLabel(node) && isGroupLabel(node)) {
				if (firstStepStarted) flushStep();
				firstStepStarted = true;
				return;
			}

			// Preceding label → flush current step and start a new one (label not rendered).
			if (consumedLabels.has(i) && precedingLabelIndices.has(i)) {
				if (firstStepStarted) flushStep();
				firstStepStarted = true;
				return;
			}

			// Other consumed labels (trailing) are silently dropped.
			if (consumedLabels.has(i)) return;

			// Form controls.
			if (isFormControl(node)) {
				if (!firstStepStarted) firstStepStarted = true;
				const fieldName = node.getAttribute('name');
				if (fieldName) currentStepFields.add(fieldName);
				currentStep.push(renderNode(node, i, labelForControl.get(i) || null));
				return;
			}

			// Everything else (h2, p, text, …).
			const rendered = renderNode(node, i);
			if (rendered == null) return;
			if (!firstStepStarted) preamble.push(rendered);
			else currentStep.push(rendered);
		});

		flushStep();

		// Append the submit button to the last step.
		if (submitButton && steps.length) steps[steps.length - 1] = [...steps[steps.length - 1], submitButton];

		return { steps, stepFields, hiddenElements, preamble };
	};

	// Preserve all attributes on the <form> element itself.
	const formAttrs = Object.fromEntries(
		Object.entries({
			action: formEl.getAttribute('action'),
			name: formEl.getAttribute('name'),
			id: formEl.getAttribute('id'),
			method: formEl.getAttribute('method'),
			acceptCharset: formEl.getAttribute('accept-charset'),
			encType: formEl.getAttribute('enctype'),
		}).filter(([, v]) => v !== null)
	);

	if (submitted) {
		if (redirect) return null;

		return (
			<div className={s.zohoForm} ref={wrapperRef}>
				<div className={s.zohoForm_success}>
					{
						successMessage ? (
							<div dangerouslySetInnerHTML={{ __html: purifier(translate(setup, successMessage)) }} />
						) : (
							<h3>{ translate(setup, 'zohoFormSuccess') }</h3>
						)
					}
				</div>
			</div>
		);
	}

	if (multistep) {
		const { steps, stepFields, hiddenElements, preamble } = buildSteps(formEl.childNodes);
		const totalSteps = steps.length;
		const isFirst = step === 0;
		const isLast = step === totalSteps - 1;

		/*
		 * Validates all required fields in the current step.
		 * Returns true if the step is clean, false (and sets errors) if not.
		 * On failure, focuses the first invalid field.
		 */
		const validateStep = () => {
			const stepErrors = validateFieldNames(stepFields[step] || new Set());
			if (Object.keys(stepErrors).length > 0) {
				setErrors((prev) => ({ ...prev, ...stepErrors }));
				// Focus first invalid field after state update
				setTimeout(() => {
					if (formRef.current) {
						const firstInvalid = formRef.current.querySelector('[aria-invalid="true"], .is_error input, .is_error select, .is_error textarea');
						if (firstInvalid) firstInvalid.focus();
					}
				}, 50);
				return false;
			}
			return true;
		};

		const goNext = () => {
			if (!validateStep()) return;
			setStep((s) => Math.min(s + 1, totalSteps - 1));
		};
		const goPrev = () => setStep((s) => Math.max(s - 1, 0));

		// Selector for elements that can normally receive keyboard focus.
		const FOCUSABLE = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

		const handleKeyDown = (e) => {
			if (e.key === 'Enter' && !isLast) {
				e.preventDefault();
				goNext();
				return;
			}

			// On Tab / Shift+Tab check whether focus is leaving the active step.
			if (e.key === 'Tab' && formRef.current) {
				const activeStep = formRef.current.querySelector(`.${s.zohoForm_step__active}`);
				if (activeStep) {
					const focusable = [...activeStep.querySelectorAll(FOCUSABLE)];
					const activeEl = document.activeElement;
					if (!e.shiftKey && focusable[focusable.length - 1] === activeEl && !isLast) {
						// Only trap Tab forward if the focused field has a value — otherwise
						// let the user tab naturally out of the form without forcing a step advance.
						const hasValue = activeEl?.tagName === 'SELECT'
							? activeEl.value && activeEl.value !== '-Select-'
							: (activeEl?.value || '').trim() !== '';
						if (hasValue) {
							e.preventDefault();
							goNext();
						}
					} else if (e.shiftKey && focusable[0] === activeEl && !isFirst) {
						e.preventDefault();
						goPrev();
					}
				}
			}
		};

		return (
			<div className={s.zohoForm} ref={wrapperRef}>
				<span aria-live="assertive" aria-atomic="true" style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>
					{`Step ${step + 1} of ${totalSteps}`}
				</span>
				<form {...formAttrs} ref={formRef} onKeyDown={handleKeyDown} onSubmit={handleSubmit}>
					{/* Intro content (headings, body copy) always visible above the steps */}
					{preamble.length > 0 && <div className={s.zohoForm_preamble}>{preamble}</div>}
					{/* Hidden inputs must always be present for form submission */}
					{hiddenElements}
					<div className={s.zohoForm_steps}>
						{steps.map((stepChildren, idx) => {
							const isActive = idx === step;
							let stepClass = s.zohoForm_step;
							if (isActive) stepClass += ` ${s.zohoForm_step__active}`;
							else if (idx < step) stepClass += ` ${s.zohoForm_step__prev}`;
							else stepClass += ` ${s.zohoForm_step__next}`;
							return (
								<div key={idx} className={stepClass} aria-hidden={!isActive || undefined} {...(!isActive ? { inert: '' } : {})}>
									{stepChildren}
								</div>
							);
						})}
					</div>
					<div className={s.zohoForm_nav}>
						{ isFirst && <span /> }
						{!isFirst && (
							<Button variantClasses={['button__back']} type="button" clickEventHandler={goPrev}><span />{ translate(setup, 'zohoFormBack') }</Button>
						)}
						{!isLast && (
							<Button variantClasses={['button__next']} type="button" clickEventHandler={goNext}>{ translate(setup, 'zohoFormNext') }<span /></Button>
						)}
					</div>
					{submitError && <p role="alert" className={s.zohoForm_error}>{ translate(setup, 'zohoFormError') }</p>}
				</form>
			</div>
		);
	}

	return (
		<div className={s.zohoForm} ref={wrapperRef}>
			<form {...formAttrs} onSubmit={handleSubmit}>
				{ renderChildren(formEl.childNodes) }
				{submitError && <p role="alert" className={s.zohoForm_error}>{ translate(setup, 'zohoFormError') }</p>}
			</form>
		</div>
	);
};

export default hot(module)(ZohoForm);
